import React, { useEffect, useState } from 'react';
import { AlertTriangle, Check, Info, X } from 'lucide-react';
import { appAlert, AppAlertItem, AppAlertType } from './appAlert';
import { t } from '../../i18n';

const TEAL = '#3fced1';
const COLORS: Record<AppAlertType, string> = { success: TEAL, error: '#ef4444', warning: '#f59e0b', info: TEAL };
const ICONS: Record<AppAlertType, React.ComponentType<{ size?: number }>> = { success: Check, error: X, warning: AlertTriangle, info: Info };
const titleFor = (type: AppAlertType): string => ({
  success: t('web.component.app-alert.erfolg'),
  error: t('web.component.app-alert.fehler'),
  warning: t('web.component.app-alert.achtung'),
  info: t('web.component.app-alert.hinweis'),
}[type]);

/**
 * Host für die zentralen Alerts – einmal in App.tsx. Zeigt den ältesten Alert mittig (Karte, 1 px Rand in der
 * Typfarbe, Icon-Kreis, Titel, Text, OK). Erfolg schließt sich nach 2,5 s selbst, alles andere bleibt bis OK/Esc.
 */
const AppAlertHost: React.FC = () => {
  const [queue, setQueue] = useState<AppAlertItem[]>([]);
  useEffect(() => appAlert.subscribe(setQueue), []);
  const current = queue[0] ?? null;

  useEffect(() => {
    if (!current || !current.autoCloseMs) return;
    const id = current.id;
    const timer = setTimeout(() => appAlert.dismiss(id), current.autoCloseMs);
    return () => clearTimeout(timer);
  }, [current]);

  useEffect(() => {
    if (!current) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' || e.key === 'Enter') appAlert.dismiss(current.id); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current]);

  if (!current) return null;
  const color = COLORS[current.type];
  const Icon = ICONS[current.type];
  const title = current.title || titleFor(current.type);
  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" role="alertdialog" aria-modal="true" aria-label={title}
      onClick={(e) => { if (e.target === e.currentTarget) appAlert.dismiss(current.id); }} data-testid="app-alert" data-alert-type={current.type}>
      <div className="w-full max-w-sm rounded-2xl border bg-zinc-900 p-6 text-center text-white shadow-2xl" style={{ borderColor: color }}>
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: `${color}1f`, color }}><Icon size={28} /></span>
        <h2 className="mt-4 text-lg font-bold tracking-tight" data-testid="app-alert-title">{title}</h2>
        <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-300" data-testid="app-alert-message">{current.message}</p>
        <button type="button" onClick={() => appAlert.dismiss(current.id)} className="mt-6 min-h-[44px] w-full rounded-xl text-sm font-semibold text-black transition hover:brightness-110" style={{ backgroundColor: color }} data-testid="app-alert-ok" autoFocus>
          {t('web.component.app-alert.ok')}
        </button>
      </div>
    </div>
  );
};

export default AppAlertHost;
